import { useState, useMemo, useEffect, useCallback, Key } from 'react';
import {
  useTranslation,
  Card,
  ENotificationType,
  useShowToastNotification,
} from '@packages/utils';
import { Table } from 'components/Base/Table';
import {
  TColumnType,
  TColumnsState,
  ETableStoragePaths,
} from 'components/Base/Table/Table.types';
import { Space } from 'components/Base/Space';
import {
  TWorkshopDetailsDataUI,
  EWorkshopPartType,
} from 'data/types/workshops.types';
import PlusMinusInput from 'components/Base/PlusMinusInput/PlusMinusInput';
import {
  useGetWorkshopPartKitOrdersQuery,
  useExportWorkshopKitOrdersMutation,
  workshopsApi,
} from 'redux/apiSlice/workshops.slice';
import { useCreateWorkshopKitOrderMutation } from 'redux/apiSlice/kitManagement.slice';
import dayjs from 'dayjs';
import {
  EditPencilOutline,
  NotebookOutline,
  RefreshOutline,
} from 'assets/icon/icon';
import IconButton from 'components/Library/Buttons/IconButton';
import { useDispatch } from 'react-redux';
import { Text } from 'components/Base/Typography';
import { Flex, Skeleton } from 'antd';
import Button from 'components/Base/Button/Button';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { useCheckPermission } from 'hooks/useCheckPermission';
import PartTabs from './PartTabs';
import { TKitInventoryItem } from './KitsTab.types';
import DateSelectModal from './DateSelectModal';
import EditOrderModal from './EditOrderModal';
import OrderHistoryModal from './OrderHistoryModal';
import NotesModal from './NotesModal';
import { getDefaultParts } from './KitsTab.data';

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  name: 'name',
  inventoryOnHand: 'inventory',
  suggestedOrder: 'suggestedOrder',
  totalOrder: 'totalOrders',
  orderDate: 'orderDate',
};

const SORT_FIELD_MAP: Record<string, string> = {
  inventoryOnHand: 'inventory',
  suggestedOrder: 'suggestedOrder',
  totalOrder: 'totalOrders',
  orderDate: 'orderDate',
};

const TRANSLATION_KEY = 'descriptions.admin-portal.workshops.details.kits';

type ModalType = 'date' | 'edit' | 'history' | 'notes' | null;

interface KitsTabProps {
  data?: TWorkshopDetailsDataUI;
}
interface TableFilters {
  name?: string[];
  [key: string]: any;
}

interface TableParams {
  pagination: {
    current: number;
    pageSize: number;
  };
  filters: TableFilters;
  sorter: {
    field: string;
    order: 'ascend' | 'descend' | null;
  };
}

const KitsTab = ({ data }: KitsTabProps) => {
  const { t } = useTranslation({
    keyPrefix: TRANSLATION_KEY,
  });

  const canEdit = useCheckPermission('PART_ORDER_EDIT');

  const dispatch = useDispatch();

  const { showToastNotification } = useShowToastNotification();
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [kitOrdersMap, setKitOrdersMap] = useState<Record<string, number>>({});

  // all modal states with a single state
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  const [selectedKit, setSelectedKit] = useState<TKitInventoryItem | null>(
    null,
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [allKitFilterOptions, setAllKitFilterOptions] = useState<
    { text: string; value: string | number }[]
  >([]);
  const [permanentKitFilterOptions, setPermanentKitFilterOptions] = useState<
    Record<string, { text: string; value: string | number }[]>
  >({});
  const [userHasSorted, setUserHasSorted] = useState(false);

  const [selectedRows, setSelectedRows] = useState<{
    selectedRowsKeys: Key[];
    selectedRowsRecords: unknown[];
  }>({
    selectedRowsKeys: [],
    selectedRowsRecords: [],
  });

  const [exportKitOrders, { isLoading: isExporting }] =
    useExportWorkshopKitOrdersMutation();

  const [createKitOrder, { isLoading: isOrderLoading }] =
    useCreateWorkshopKitOrderMutation();

  useEffect(() => {
    setIsTableLoading(isOrderLoading);
    if (isOrderLoading) return undefined;
    const timer = setTimeout(() => setIsTableLoading(false), 500);
    return () => clearTimeout(timer);
  }, [isOrderLoading]);

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
    filters: {},
    sorter: {
      field: '',
      order: null,
    },
  });

  const getBackendSortField = (field: string): string => {
    const fieldMap: Record<string, string> = {
      inventoryOnHand: 'inventory',
      suggestedOrder: 'suggestedOrder',
      totalOrder: 'totalOrders',
      orderDate: 'orderDate',
    };
    return fieldMap[field] || field;
  };

  const parts = useMemo(() => {
    return data?.parts && data.parts.length > 0
      ? data.parts
      : getDefaultParts();
  }, [data?.parts]);

  const partTypesList = useMemo(() => {
    return parts.map(part => part.partType);
  }, [parts]);

  const activePartType = partTypesList[activePartIndex];
  const computedPartId = useMemo(() => {
    const activePart = parts[activePartIndex];
    if (activePart?.workshopPartId) {
      return activePart.workshopPartId;
    }

    const fallbackId = `${data?.id || 'unknown'}-${activePartType}`.replace(
      /[^a-zA-Z0-9-]/g,
      '',
    );
    return fallbackId;
  }, [parts, activePartIndex, data?.id, activePartType]);

  const {
    data: kitOrdersData,
    isLoading: isKitOrdersLoading,
    isFetching: isKitOrdersFetching,
    refetch,
  } = useGetWorkshopPartKitOrdersQuery(
    {
      workshopId: data?.id || '',
      ...(tableParams.sorter.order === 'ascend' ||
      tableParams.sorter.order === 'descend'
        ? {
            sortField: getBackendSortField(tableParams.sorter.field),
            sortDirection:
              tableParams.sorter.order === 'descend' ? 'DESC' : 'ASC',
          }
        : {}),
      kitIds: tableParams.filters.name?.join(','),
    },
    {
      skip: !data?.id,
    },
  );

  const isTableLoadingState = useMemo(
    () =>
      isKitOrdersLoading ||
      isKitOrdersFetching ||
      isOrderLoading ||
      isTableLoading,
    [isKitOrdersLoading, isKitOrdersFetching, isOrderLoading, isTableLoading],
  );

  const shippingAddress = useMemo(() => {
    const shipment =
      kitOrdersData?.shipmentLocation || data?.shipmentLocation || null;

    return shipment?.name && shipment?.address?.formattedAddress
      ? `${shipment.name} | ${shipment.address.formattedAddress}`
      : '';
  }, [
    kitOrdersData?.shipmentLocation?.name,
    kitOrdersData?.shipmentLocation?.address?.formattedAddress,
    data?.shipmentLocation?.name,
    data?.shipmentLocation?.address?.formattedAddress,
  ]);

  const kitInventoryData = useMemo(() => {
    if (isKitOrdersLoading || !kitOrdersData) {
      return [];
    }

    const itemsForPart = kitOrdersData.items[activePartType] || [];

    return itemsForPart.map(item => {
      const { kitId, kitOrderId, name, suggestedOrder, inventory, orderDate } =
        item;

      const kitOrder = kitOrdersMap[kitId];
      const totalOrder = kitOrder || item.totalOrders || 0;

      const formattedOrderDate = orderDate
        ? dayjs(item.orderDate).format('MMM DD, YYYY')
        : 'N/A';

      const hasExistingOrder = kitOrderId !== null;

      return {
        key: kitId,
        name,
        inventoryOnHand: inventory || 0,
        suggestedOrder,
        totalOrder,
        orderDate: formattedOrderDate,
        hasExistingOrder,
        kitOrderId: item.kitOrderId || null,
        disabled: totalOrder <= 0,
      };
    });
  }, [activePartType, kitOrdersMap, kitOrdersData, isKitOrdersLoading]);

  const handlePartChange = (index: number) => {
    setUserHasSorted(false);
    setTableParams({
      pagination: tableParams.pagination,
      filters: {},
      sorter: {
        field: '',
        order: null,
      },
    });
    setActivePartIndex(index);
  };

  const handleTotalOrderChange = useCallback((kitId: string, value: number) => {
    setKitOrdersMap(prevState => ({
      ...prevState,
      [kitId]: value,
    }));
  }, []);

  const openModal = (
    type: ModalType,
    kit?: TKitInventoryItem,
    editMode = false,
  ) => {
    if (kit) {
      setSelectedKit(kit);
      setIsEditMode(editMode);
    }
    setActiveModal(type);
  };

  const handleShowHistory = () => openModal('history');
  const handleOrderClick = (record: TKitInventoryItem) =>
    openModal('date', record, false);
  const handleEditClick = (record: TKitInventoryItem) =>
    openModal('edit', record, true);
  const handleShowNotesModal = () => openModal('notes');

  const handleCloseModal = () => {
    setActiveModal(null);
    setSelectedKit(null);
    setIsEditMode(false);
  };

  const getStoragePath = useCallback(() => {
    return `${ETableStoragePaths.KIT_MANAGEMENT_WORKSHOP}_${
      data?.id || 'unknown'
    }_${activePartType}`;
  }, [data?.id, activePartType]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const newSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (newSorter && newSorter.order) {
      setUserHasSorted(true);
    }

    const newField = newSorter?.field || '';
    const newOrder = newSorter?.order || null;

    setTableParams({
      pagination,
      filters,
      sorter: {
        field: newField,
        order: newOrder,
      },
    });
  };

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[getStoragePath()] as TColumnsState[];
    if (!columnsState) {
      return Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    }
    return columnsState
      .filter(col => col.show)
      .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
      .filter(Boolean);
  }, [getStoragePath]);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {
        workshopId: data?.id,
        partType: activePartType,
      };
      if (ids?.length) {
        filters.kitIds = ids;
      }
      if (tableParams.filters.name?.length) {
        filters.kitIds = tableParams.filters.name;
      }
      return filters;
    },
    [data?.id, activePartType, tableParams.filters],
  );

  const buildExportSort = useCallback(() => {
    if (!tableParams.sorter.field || !tableParams.sorter.order)
      return undefined;
    const field = SORT_FIELD_MAP[tableParams.sorter.field];
    if (!field) return undefined;
    return {
      field,
      direction: tableParams.sorter.order === 'descend' ? 'DESC' : 'ASC',
    };
  }, [tableParams.sorter]);

  const handleExportAll = useCallback(async () => {
    try {
      const sort = buildExportSort();
      await exportKitOrders({
        columns: getVisibleExportColumns(),
        filters: buildExportFilters(),
        ...(sort && { sort }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportKitOrders,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    const { selectedRowsKeys } = selectedRows;
    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }
    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const sort = buildExportSort();
      await exportKitOrders({
        columns: getVisibleExportColumns(),
        filters: buildExportFilters(ids),
        ...(sort && { sort }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    selectedRows,
    exportKitOrders,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const hasFilters = useMemo(() => {
    return (
      Object.keys(tableParams.filters).length > 0 ||
      Boolean(tableParams.sorter.field && tableParams.sorter.order)
    );
  }, [tableParams.filters, tableParams.sorter]);

  const handleFiltersReset = () => {
    setUserHasSorted(false);
    setTableParams({
      pagination: tableParams.pagination,
      filters: {},
      sorter: {
        field: '',
        order: null,
      },
    });
  };

  useEffect(() => {
    if (data?.id) {
      refetch();
    }
  }, [data?.id, activePartType]);

  useEffect(() => {
    if (kitOrdersData?.items && kitOrdersData.items[activePartType]) {
      const options = kitOrdersData.items[activePartType].map(item => ({
        text: item.name,
        value: item.kitId,
      }));

      setPermanentKitFilterOptions(prev => {
        if (!prev[activePartType]) {
          return { ...prev, [activePartType]: options };
        }
        return prev;
      });

      setAllKitFilterOptions(
        permanentKitFilterOptions[activePartType] || options,
      );
    }
  }, [kitOrdersData, activePartType, permanentKitFilterOptions]);

  const handleCreateOrUpdateOrder = async (params: {
    totalOrder?: number;
    date?: string;
    isEdit: boolean;
  }) => {
    if (!selectedKit || !data?.id) return;

    setIsTableLoading(true);

    try {
      // Determine the order count based on whether it's coming from edit modal or date modal
      const orderCount =
        params.totalOrder !== undefined
          ? params.totalOrder
          : selectedKit.totalOrder;

      // Determine the date format based on the source
      let formattedDate: string;
      if (params.date) {
        formattedDate = dayjs(params.date, 'MMMM DD, YYYY').format(
          'MM/DD/YYYY',
        );
      } else if (selectedKit.orderDate === 'N/A') {
        formattedDate = dayjs().format('MM/DD/YYYY');
      } else {
        formattedDate = dayjs(selectedKit.orderDate, 'MMM DD, YYYY').format(
          'MM/DD/YYYY',
        );
      }

      // Determine kitOrderId parameter
      const kitOrderIdParam =
        params.isEdit && selectedKit.kitOrderId ? selectedKit.kitOrderId : null;

      const response = await createKitOrder({
        workshopId: data.id,
        workshopKitId: selectedKit.key,
        workshopPart: activePartType,
        orderCount,
        orderDate: formattedDate,
        kitOrderId: kitOrderIdParam,
      }).unwrap();

      if (response.kitOrderId) {
        setKitOrdersMap(prevMap => ({
          ...prevMap,
          [selectedKit.key]: response.totalOrders,
        }));

        // Create a patch to update the cache
        const patchResult = workshopsApi.util.updateQueryData(
          'getWorkshopPartKitOrders',
          {
            workshopId: data.id,
            ...(tableParams.sorter.order === 'ascend' ||
            tableParams.sorter.order === 'descend'
              ? {
                  sortField: getBackendSortField(tableParams.sorter.field),
                  sortDirection:
                    tableParams.sorter.order === 'descend' ? 'DESC' : 'ASC',
                }
              : {}),
            kitIds: tableParams.filters.name?.join(','),
          },
          draft => {
            if (!draft?.items || !draft.items[activePartType]) return;

            const itemToUpdate = draft.items[activePartType].find(
              item => item.kitId === selectedKit.key,
            );

            if (itemToUpdate) {
              itemToUpdate.inventory =
                response.inventory || itemToUpdate.inventory;
              itemToUpdate.suggestedOrder =
                response.suggestedOrder || itemToUpdate.suggestedOrder;
              itemToUpdate.totalOrders = orderCount;
              itemToUpdate.orderDate = formattedDate;
              itemToUpdate.kitOrderId = response.kitOrderId;
            }
          },
        );
        // @ts-ignore
        dispatch(patchResult);
      }

      showToastNotification({
        message: `Order for ${selectedKit.name} has been ${
          params.isEdit ? 'updated' : 'placed'
        } successfully`,
        type: ENotificationType.SUCCESS,
      });

      handleCloseModal();
    } catch (error) {
      showToastNotification({
        message: `Failed to ${
          params.isEdit ? 'update' : 'place'
        } order. Please try again.`,
        type: ENotificationType.ERROR,
      });
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleSaveDate = async (date: string) => {
    await handleCreateOrUpdateOrder({ date, isEdit: isEditMode });
  };

  const handleSaveEditOrder = async (totalOrder: number) => {
    await handleCreateOrUpdateOrder({ totalOrder, isEdit: true });
  };

  const kitsColumns: TColumnType<TKitInventoryItem>[] = useMemo(
    () => [
      {
        title: t('table.KIT_INVENTORY_NAME', 'Kit Inventory Name'),
        dataIndex: 'name',
        key: 'name',
        filters: allKitFilterOptions || [],
        filteredValue: (tableParams.filters.name as string[]) || null,
        render: (_, record) => <span data-cy="kitName_txt">{record.name}</span>,
      },
      {
        title: t('table.INV_ON_HAND', 'Inv. On-hand'),
        dataIndex: 'inventoryOnHand',
        key: 'inventoryOnHand',
        align: 'center',
        sorter: true,
        sortOrder:
          userHasSorted && tableParams.sorter.field === 'inventoryOnHand'
            ? tableParams.sorter.order
            : null,
        render: (_, record) => (
          <span data-cy="inv_onHand_txt">{record.inventoryOnHand}</span>
        ),
      },
      {
        title: t('table.SUGGESTED_ORDER', 'Suggested Order'),
        dataIndex: 'suggestedOrder',
        key: 'suggestedOrder',
        align: 'center',
        sorter: true,
        sortOrder:
          userHasSorted && tableParams.sorter.field === 'suggestedOrder'
            ? tableParams.sorter.order
            : null,
        render: (_, record) => (
          <span data-cy="suggestedOrder_txt">{record.suggestedOrder}</span>
        ),
      },
      {
        title: t('table.TOTAL_ORDER', 'Total Order'),
        dataIndex: 'totalOrder',
        key: 'totalOrder',
        render: (_, record) => {
          if (record.hasExistingOrder) {
            return (
              <Text style={{ fontFamily: 'Montserrat' }}>
                {record.totalOrder}
              </Text>
            );
          }
          return (
            <Flex justify="center">
              <PlusMinusInput
                value={record.totalOrder || 0}
                onChange={value => handleTotalOrderChange(record.key, value)}
                min={0}
                width={100}
                disabled={!canEdit}
              />
            </Flex>
          );
        },
        align: 'center',
        sorter: true,
        sortOrder:
          userHasSorted && tableParams.sorter.field === 'totalOrder'
            ? tableParams.sorter.order
            : null,
      },
      {
        title: t('table.ORDER_DATE', 'Order Date'),
        dataIndex: 'orderDate',
        key: 'orderDate',
        align: 'center',
        sorter: true,
        sortOrder:
          userHasSorted && tableParams.sorter.field === 'orderDate'
            ? tableParams.sorter.order
            : null,
      },
      {
        title: t('table.ACTIONS', 'Actions'),
        key: 'actions',
        render: (_, record) => (
          <Flex align="center" justify="center">
            {record.hasExistingOrder ? (
              <IconButton
                icon={EditPencilOutline}
                size={16}
                defaultFill="#656565"
                hoverFill="#333333"
                onClick={() => handleEditClick(record)}
                ariaLabel="Edit order"
                data-cy="orderStatus"
                disabled={!canEdit}
              />
            ) : (
              <Button
                variant="primary"
                size="small"
                disabled={record.disabled || !canEdit}
                onClick={() => handleOrderClick(record)}
                data-cy="orderStatus"
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {t('table.ORDER_BUTTON', 'Order')}
              </Button>
            )}
          </Flex>
        ),
        align: 'center',
      },
    ],
    [
      t,
      userHasSorted,
      tableParams.sorter.field,
      tableParams.sorter.order,
      tableParams.filters.name,
      allKitFilterOptions,
      handleTotalOrderChange,
    ],
  );

  const tableHeaderActions = (
    <Space>
      <Button
        icon={<NotebookOutline size={24} />}
        variant="default"
        onClick={handleShowNotesModal}
        data-cy="addNote_btn"
        disabled={!canEdit}
        style={{ fontWeight: 600 }}
      >
        {t('buttons.ADD_NOTE', 'Add Note')}
      </Button>
      <Button
        icon={<RefreshOutline size={24} />}
        variant="default"
        onClick={handleShowHistory}
        data-cy="showHistory_btn"
        style={{ fontWeight: 600 }}
      >
        {t('buttons.SHOW_HISTORY', 'Show history')}
      </Button>
      <ExportDropdown
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />
    </Space>
  );

  const getSortOrder = (direction?: string): 'ascend' | 'descend' | null => {
    if (direction === 'DESC') return 'descend';
    if (direction === 'ASC') return 'ascend';
    return null;
  };

  const getSortDirection = (
    order?: 'ascend' | 'descend' | null,
  ): string | undefined => {
    if (order === 'descend') return 'DESC';
    if (order === 'ascend') return 'ASC';
    return undefined;
  };

  useEffect(() => {
    const storageFilters = appLocalStorage.getItem(EStorageNames.filters);
    const currentPath = getStoragePath();

    if (storageFilters && storageFilters[currentPath]) {
      const savedSettings = storageFilters[currentPath];

      setTableParams(prev => ({
        pagination: {
          current: savedSettings.page
            ? savedSettings.page + 1
            : prev.pagination.current,
          pageSize: savedSettings.pageSize || prev.pagination.pageSize,
        },
        filters: savedSettings.selectedFilters || {},
        sorter: {
          field: savedSettings.sortColumn?.sortField || '',
          order: getSortOrder(savedSettings.sortColumn?.sortDirection),
        },
      }));

      if (savedSettings.sortColumn && savedSettings.sortColumn.sortField) {
        setUserHasSorted(true);
      }
    }
  }, [activePartType, getStoragePath]);

  useEffect(() => {
    const storageFilters = appLocalStorage.getItem(EStorageNames.filters) || {};
    const currentPath = getStoragePath();

    appLocalStorage.setItem(EStorageNames.filters, {
      ...storageFilters,
      [currentPath]: {
        ...(storageFilters[currentPath] || {}),
        page: tableParams.pagination.current - 1,
        pageSize: tableParams.pagination.pageSize,
        sortColumn: {
          sortField: tableParams.sorter.field,
          sortDirection: getSortDirection(tableParams.sorter.order),
        },
        selectedFilters: tableParams.filters,
      },
    });
  }, [tableParams, getStoragePath]);

  return (
    <div>
      <PartTabs
        parts={parts}
        activePartIndex={activePartIndex}
        onPartChange={handlePartChange}
      />

      <Card styles={{ body: { padding: 0 } }}>
        <Table
          key={`kits-table-${activePartIndex}-${data?.id}`}
          dataSource={kitInventoryData}
          tableTitle="Kits"
          columns={kitsColumns as TColumnType<unknown>[]}
          pagination={{
            pageSize: tableParams.pagination.pageSize,
            current: tableParams.pagination.current,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50'],
            showTotal: total =>
              `${t('table.TOTAL_ITEMS', `Total ${total} items`)}`,
          }}
          storagePath={getStoragePath()}
          headerActions={tableHeaderActions}
          loading={isTableLoadingState}
          onChange={handleTableChange}
          hasFilters={hasFilters}
          onFiltersReset={handleFiltersReset}
          tabHeight={170}
          rowKey={record => (record as TKitInventoryItem).key}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />
      </Card>

      <Card className="mt-4">
        <Text style={{ fontFamily: 'Montserrat' }} fontWeight={600}>
          {t(
            'SHIP_TO_NOTE',
            'Inventory availability is based on the Location:',
          )}{' '}
          &nbsp;
        </Text>
        {isKitOrdersLoading || isKitOrdersFetching ? (
          <Skeleton.Input active size="small" style={{ width: 420 }} />
        ) : (
          <Text
            style={{ fontFamily: 'Montserrat' }}
            data-cy="shippingAddress_txt"
          >
            {shippingAddress}
          </Text>
        )}
      </Card>

      <DateSelectModal
        visible={activeModal === 'date'}
        onClose={handleCloseModal}
        onSave={handleSaveDate}
        address={shippingAddress}
        isLoading={isOrderLoading}
      />

      {selectedKit && (
        <EditOrderModal
          visible={activeModal === 'edit'}
          onClose={handleCloseModal}
          onSave={handleSaveEditOrder}
          kitName={selectedKit.name}
          currentTotalOrder={selectedKit.totalOrder}
          currentDate={
            selectedKit.orderDate === 'N/A'
              ? null
              : selectedKit.orderDate || null
          }
          address={shippingAddress}
          isLoading={isOrderLoading}
        />
      )}

      <OrderHistoryModal
        visible={activeModal === 'history'}
        onClose={handleCloseModal}
        workshopId={data?.id || ''}
        activePart={partTypesList[activePartIndex] as EWorkshopPartType}
      />

      <NotesModal
        key={`notes-modal-${computedPartId}`}
        visible={activeModal === 'notes'}
        onClose={handleCloseModal}
        workshopId={data?.id || ''}
        partId={computedPartId}
      />
    </div>
  );
};

export default KitsTab;
